/*
 * LABELS search analytics
 * Publishes labels_search_* Shopify custom events (picked up by the "LABELS Search Analytics"
 * custom pixel) and keeps the 24-hour search attribution that becomes the hidden _ls_search
 * line-item property. GA4 itself is only ever fed server-side; nothing here talks to Google.
 * Spec: docs/superpowers/specs/2026-10-17-search-analytics-design.md
 */
(() => {
  if (window.LabelsSearchAnalytics) return;

  const EVENTS = new Set(['open', 'query', 'no_results', 'select_item', 'add_to_cart', 'refine', 'view_all', 'engage', 'close'].map((n) => 'labels_search_' + n));
  // These are followed by a page load that can kill the pixel sandbox before it posts.
  const NAVIGATES = new Set(['labels_search_select_item', 'labels_search_view_all']);
  const ATTR_KEY = 'labels-search:attr';
  const OUTBOX_KEY = 'labels-search:outbox';
  const ATTR_TTL_MS = 24 * 3600e3;
  const ATTR_MAX = 50;
  const PROP_NAME = 'properties[_ls_search]';
  const PROP_MAX = 255;
  const POLL_MS = 250;
  const POLL_MAX = 80;

  const rid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  const read = (store, key, d) => { try { const v = store.getItem(key); return v ? JSON.parse(v) : d; } catch (_) { return d; } };
  const write = (store, key, v) => { try { store.setItem(key, JSON.stringify(v)); } catch (_) {} };
  const drop = (store, key) => { try { store.removeItem(key); } catch (_) {} };

  const normTerm = (s) => String(s == null ? '' : s)
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100);

  let currentSearch = '';
  const newSearch = () => (currentSearch = rid());
  const searchId = () => currentSearch || newSearch();

  /* ---------- Publishing ---------- */
  const queue = [];
  let polls = 0;
  let polling = false;

  function publisher() {
    const a = window.Shopify && window.Shopify.analytics;
    return a && typeof a.publish === 'function' ? a : null;
  }

  function flush() {
    polling = false;
    const a = publisher();
    if (!a) {
      if (polls++ < POLL_MAX && queue.length) { polling = true; setTimeout(flush, POLL_MS); }
      return;
    }
    while (queue.length) {
      const [name, data] = queue.shift();
      try { a.publish(name, data); } catch (_) {}
    }
  }

  function enqueue(name, data) {
    queue.push([name, data]);
    if (!polling) flush();
  }

  function pageType() {
    const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    return (meta && meta.page && meta.page.pageType) || '';
  }

  function track(name, params) {
    if (!EVENTS.has(name)) return;
    try {
      const data = Object.assign({}, params || {}, {
        event_id: rid(),
        search_id: searchId(),
        page_type: pageType(),
        locale: (document.documentElement && document.documentElement.lang) || '',
        ts: Date.now(),
      });
      ['search_term', 'last_term'].forEach((k) => { if (k in data) data[k] = normTerm(data[k]); });
      if (NAVIGATES.has(name)) {
        const box = read(sessionStorage, OUTBOX_KEY, []);
        box.push([name, data]);
        write(sessionStorage, OUTBOX_KEY, box.slice(-20));
      }
      enqueue(name, data);
    } catch (_) {}
  }

  function replayOutbox() {
    const box = read(sessionStorage, OUTBOX_KEY, []);
    drop(sessionStorage, OUTBOX_KEY);
    box.forEach((x) => { if (Array.isArray(x) && EVENTS.has(x[0]) && x[1]) enqueue(x[0], Object.assign({}, x[1], { replay: true })); });
  }

  /* ---------- Attribution ---------- */
  function attrList() {
    const now = Date.now();
    const raw = read(localStorage, ATTR_KEY, []);
    const list = (Array.isArray(raw) ? raw : []).filter((x) => x && x.id && now - x.ts < ATTR_TTL_MS);
    if (list.length !== (Array.isArray(raw) ? raw.length : 0)) write(localStorage, ATTR_KEY, list);
    return list;
  }

  function remember(productId, info) {
    if (productId == null || productId === '') return;
    const id = String(productId);
    const entry = { id, sid: searchId(), q: normTerm(info && info.q), pos: Number(info && info.pos) || 0, ts: Date.now() };
    write(localStorage, ATTR_KEY, [entry, ...attrList().filter((x) => x.id !== id)].slice(0, ATTR_MAX));
  }

  function propertyFor(productId) {
    if (productId == null) return null;
    const e = attrList().find((x) => x.id === String(productId));
    if (!e) return null;
    const tail = `|sid=${e.sid}|p=${e.pos}`;
    const q = e.q.replace(/\|/g, ' ').replace(/\s+/g, ' ').trim().slice(0, PROP_MAX - 2 - tail.length).trim();
    return `q=${q}${tail}`;
  }

  function injectInto(comp) {
    const prop = propertyFor(comp && comp.dataset && comp.dataset.productId);
    const form = prop && comp.querySelector('form');
    if (!form) return;
    let input = form.querySelector(`input[name="${PROP_NAME}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = PROP_NAME;
      form.appendChild(input);
    }
    input.value = prop;
  }

  function injectForms(root) {
    try { (root || document).querySelectorAll('product-form-component[data-product-id]').forEach(injectInto); } catch (_) {}
  }

  function closestForm(e, sel) {
    const t = e.target;
    if (!t || typeof t.closest !== 'function') return null;
    if (sel && !t.closest(sel)) return null;
    return t.closest('product-form-component[data-product-id]');
  }

  // Capture phase: runs before product-form-component builds its FormData, and before the
  // dynamic checkout button reads the form.
  document.addEventListener('submit', (e) => { const c = closestForm(e); if (c) injectInto(c); }, true);
  document.addEventListener('click', (e) => { const c = closestForm(e, '.shopify-payment-button'); if (c) injectInto(c); }, true);

  window.LabelsSearchAnalytics = { track, newSearch, searchId, normTerm, remember, propertyFor, injectForms };

  replayOutbox();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => injectForms());
  else injectForms();
})();
